import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAxios } from "../../../API/fetchAxios";
import {
  accountIdsMatch,
  emitIbActiveAccountChanged,
  getStoredSelectedAccountId,
  setStoredSelectedAccountId,
} from "../../../Constants/accountTypes";
import { useAuthStore } from "../../../Contexts/AuthContext";
import { fetchOperatingAccounts } from "../../../Services/operatingAccountsApi";
import { isIbHomeSelectableAccount } from "../../../Utils/accountPixEligibility";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { TransferAccountSelector } from "../TransferAccountSelector";

const activateIbAccount = (accountId) =>
  fetchAxios({
    verb: "POST",
    context: "activateAccount",
    id: accountId,
    responseType: "json",
    cancelToken: undefined,
  });

/**
 * IB home account picker — person-scoped accounts from MobileService
 * GET /v1/ib/accounts (not Console /baas instance accounts).
 *
 * Always renders a dropdown (works with ~20 accounts). On change:
 * stores selection, POSTs active_account, then notifies parent to reload saldo/extrato.
 */
export const HomeAccountSelector = ({
  individualAccount: _individualAccount = undefined,
  onActiveAccountChange,
  /** Defaults to IB home rules; pass `isPixEligibleAccount` on `/pix`. */
  filterAccount = isIbHomeSelectableAccount,
} = {}) => {
  void _individualAccount;

  const isAuthenticated = useAuthStore((state) => state.isAutenticated);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [selectedId, setSelectedId] = useState(getStoredSelectedAccountId);
  const activatedRef = useRef(null);
  const accountsRef = useRef([]);
  const onChangeRef = useRef(onActiveAccountChange);
  const filterAccountRef = useRef(filterAccount);
  const loadGenRef = useRef(0);
  onChangeRef.current = onActiveAccountChange;
  filterAccountRef.current = filterAccount;
  accountsRef.current = accounts;

  const persistAndActivate = useCallback(async (accountId, { notify } = { notify: true }) => {
    const nextId = accountId != null && accountId !== "" ? String(accountId) : null;
    setSelectedId(nextId);
    // Persist before GET saldo so query `account_id` is the newly selected account.
    // Emit after POST active_account so `/me` / proxy fallback also match.
    setStoredSelectedAccountId(nextId, { emitEvent: false });
    if (!nextId) return false;

    const selected = accountsRef.current.find((account) => String(account.id) === nextId);
    if (selected?.features) {
      useIbProductFeaturesStore.getState().setFeatures(selected.features);
    }

    if (activatedRef.current === nextId) {
      if (notify) onChangeRef.current?.(nextId);
      return true;
    }

    emitIbActiveAccountChanged(nextId);

    setActivating(true);
    try {
      await activateIbAccount(nextId);
      activatedRef.current = nextId;
      emitIbActiveAccountChanged(nextId);
      if (notify) onChangeRef.current?.(nextId);
      return true;
    } catch {
      // Selection stays client-side; saldo GETs send account_id even if SetAppActive lagged.
      emitIbActiveAccountChanged(nextId);
      return false;
    } finally {
      setActivating(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccounts([]);
      setLoading(false);
      return undefined;
    }

    const loadGen = ++loadGenRef.current;
    setLoading(true);

    (async () => {
      let nextId = null;
      let shouldActivate = false;

      try {
        const loaded = await fetchOperatingAccounts();
        if (loadGen !== loadGenRef.current) return;

        const mapped = (Array.isArray(loaded) ? loaded : []).filter(
          (account) => account.id && filterAccountRef.current(account),
        );
        setAccounts(mapped);

        const stored = getStoredSelectedAccountId();
        const storedMatch = stored != null && stored !== ""
          ? mapped.find((account) =>
              accountIdsMatch(account.id, stored)
              || accountIdsMatch(account.accountNumber, stored)
              || accountIdsMatch(account.raw?.account_number, stored))
          : null;
        const wanted = storedMatch?.id ? String(storedMatch.id) : null;
        const serverActive = mapped.find((account) => account.raw?.set_app_active);
        nextId = wanted || (serverActive?.id ? String(serverActive.id) : null) || mapped[0]?.id || null;

        if (!nextId) {
          setSelectedId(null);
          return;
        }

        setSelectedId(nextId);
        setStoredSelectedAccountId(nextId);

        // fetchOperatingAccounts already syncs features; keep a post-load effect path
        // if list sync was skipped so menu gates match the selected account.
        const selected = mapped.find((account) => String(account.id) === String(nextId));
        if (selected?.features) {
          useIbProductFeaturesStore.getState().setFeatures(selected.features);
        }

        const alreadyActive = mapped.some(
          (account) => String(account.id) === nextId && account.raw?.set_app_active,
        );
        if (alreadyActive) {
          activatedRef.current = nextId;
        } else {
          shouldActivate = true;
        }
      } catch (err) {
        if (loadGen !== loadGenRef.current) return;
        // 401 → fetchAxios dispatches error-401; PrivateApp logs out — don't flash empty state.
        if (err?.response?.status === 401) return;
        setAccounts([]);
      } finally {
        if (loadGen === loadGenRef.current) setLoading(false);
      }

      if (loadGen !== loadGenRef.current || !nextId) return;

      // Activate when server active_account must change. Do not notify parent on
      // mount — header hard-reloads on user switch; notifying here loops reload.
      // setStoredSelectedAccountId above already fires IB_ACTIVE_ACCOUNT_CHANGED_EVENT.
      if (shouldActivate) {
        void persistAndActivate(nextId, { notify: false });
      }
    })();

    return () => {
      // Invalidate this generation so late responses do not overwrite a newer load.
      loadGenRef.current += 1;
    };
  }, [persistAndActivate, isAuthenticated]);

  const handleChange = useCallback(
    (accountId) => {
      void persistAndActivate(accountId, { notify: true });
    },
    [persistAndActivate],
  );

  const emptyLabel = useMemo(
    () => (loading ? "Carregando contas…" : "Nenhuma conta disponível"),
    [loading],
  );

  return (
    <TransferAccountSelector
      accounts={accounts}
      value={selectedId}
      onChange={handleChange}
      loading={loading}
      disabled={loading || activating}
      emptyLabel={emptyLabel}
      ariaLabel="Minha conta"
      variant="select"
    />
  );
};

export { useHomeIndividualAccount } from "./useHomeIndividualAccount";
